import { Injectable } from '@angular/core';
import { AuthorizationServiceConfiguration } from '@openid/appauth/built/authorization_service_configuration';
import { TokenRequestHandler, BaseTokenRequestHandler } from '@openid/appauth/built/token_request_handler';
import { AuthorizationRequest } from '@openid/appauth/built/authorization_request';
import { TokenRequest, GRANT_TYPE_AUTHORIZATION_CODE, GRANT_TYPE_REFRESH_TOKEN } from '@openid/appauth/built/token_request';
import { TokenResponse, TokenResponseJson } from '@openid/appauth/built/token_response';

import { AngularRequestor } from './AngularRequestor'
import { SecretsStore } from './SecretsStore'
import { Identity } from './Identity';
import { NucleusIdentityConfig } from './NucleusIdentityConfig';
import { NucleusAuthorizationHandler } from './NucleusAuthorizationHandler';
import { Crypto } from '@openid/appauth/built/crypto_utils';
import { NucleusCrypto } from './NucleusCrypto';

@Injectable({
  providedIn: 'root'
})
export class NucleusIdentityService {

  private configuration: AuthorizationServiceConfiguration = null;
  private authorizationHandler: NucleusAuthorizationHandler = null;
  private tokenHandler: TokenRequestHandler;
  private readonly tokenStorageKey = "Nucleus.Identity.Token"
  private tokenResponse: TokenResponse;
  private crypto: Crypto;

  public identity: Identity = null;
  
  public get isAuthenticated(): boolean {
    return this.tokenResponse != null;
  }

  constructor(private requestor: AngularRequestor, private secretsStore: SecretsStore, private config: NucleusIdentityConfig) {
    this.tokenHandler = new BaseTokenRequestHandler(requestor)
    this.crypto = new NucleusCrypto(); //TODO: LiteCrypto is a temporary replacement for DefaultCrypto until this is published: https://github.com/openid/AppAuth-JS/pull/87/ 
    this.authorizationHandler = new NucleusAuthorizationHandler(secretsStore, this.crypto);
  }
  
  public async init(startLogin: boolean = false): Promise<void> {
    var res = await this.handleLogin()
    if (!res) {
      //try login from storage
      var jr = this.secretsStore.getJson<TokenResponseJson>(this.tokenStorageKey)
      if (jr) {

        var r = new TokenResponse(jr);
        if (r && (r.accessToken || r.idToken)) {
          this.setTokenResponse(r, false);
          if (this.tokenResponse.isValid()) {
            return;
          }

          if (await this.loginWithRefreshToken()) {
            return;
          }
        }
      }

      if (startLogin) {
        this.login();
      }
    }
  }

  public async login() {
    await this.assureConfiguration();

    var request = new AuthorizationRequest({
      client_id: this.config.clientId,
      redirect_uri: this.getRedirectUri(),
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      scope: "openid offline_access",
      extras: {
        'response_mode': 'fragment',
        'prompt': 'consent',
        'access_type': 'offline'
      }
    }, this.crypto, true);
    this.authorizationHandler.performAuthorizationRequest(this.configuration, request);
  }

  public logout() {
    this.setTokenResponse(null, true);
  }

  public async getAccessToken(): Promise<string> {
    var tr = this.tokenResponse;
    if (tr.accessToken) {
      if (!tr.isValid()) {
        await this.loginWithRefreshToken();
      }
      return tr.accessToken;
    }
    return null;
  }

  private getRedirectUri() : string
  {
    var uri: string;
    if (this.config.redirectUrl) {
      uri = this.config.redirectUrl;
    }
    uri = window.location.href;
    var i = uri.indexOf("#");
    if (i > 0) {
      uri = uri.substring(0, i);
    }
    return uri;
  }

  private async assureConfiguration() {
    if (this.configuration != null) {
      return;
    }

    var authority = this.config.authority;
    if (authority[authority.length - 1] == '/') {
      authority = authority.slice(0, -1);
    }

    try {
      this.configuration = await AuthorizationServiceConfiguration.fetchFromIssuer(authority, this.requestor);
    }
    catch (e) {
      console.log("NucleusIdentityClient: Cannot load OIDC configuration: " + (<Error>e).message);
      throw e;
    }
  }

  private async handleLogin() {
    var res = await this.authorizationHandler.completeAuthorizationRequest();
    if (res) {
      await this.assureConfiguration();
      var verifier = res.request.internal["code_verifier"];
      var tokenReq = new TokenRequest({
        client_id: this.config.clientId,
        redirect_uri: this.getRedirectUri(),
        grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
        code: res.response.code,
        extras: {
          "code_verifier": verifier
        }
      });

      await this.performTokenRequest(tokenReq);
      window.location.hash = ""; // we remove the hash from url so code is hidden
      return true;
    }
    return false;
  }


  private async loginWithRefreshToken(): Promise<boolean> {
    await this.assureConfiguration();

    if (this.tokenResponse.refreshToken) {
      var tokenReq = new TokenRequest({
        client_id: this.config.clientId,
        redirect_uri: this.getRedirectUri(),
        grant_type: GRANT_TYPE_REFRESH_TOKEN,
        refresh_token: this.tokenResponse.refreshToken
      });

      try {
        await this.performTokenRequest(tokenReq);
        return true;
      } catch (e) {
        console.warn("Failed to login with refresh token.", e);

        return false;
      }
    } else {
      console.warn("There is no refresh token available.")
    }
    this.logout();
    return false;
  }

  private async performTokenRequest(req: TokenRequest) {
    var r = await this.tokenHandler.performTokenRequest(this.configuration, req);
    this.setTokenResponse(r, true);
  }

  private setTokenResponse(res: TokenResponse, save: boolean) {
    this.tokenResponse = res;
    if (res == null) {
      this.identity = null;
      this.secretsStore.remove(this.tokenStorageKey);

    } else {
      this.identity = Identity.createFromResponse(this.tokenResponse);
      if (save) {
        this.secretsStore.setJson(this.tokenStorageKey, this.tokenResponse.toJson());
      }
    }
  }
}
